export const problems = [
  {
    slug: "catastro-escritura-no-coinciden",
    ogImage: "/og/problema-catastro-escritura-no-coinciden.jpg",
    shortName: "Catastro y escritura no coinciden",
    seoTitle: "Catastro y escritura no coinciden: qué hacer | MideFinca",
    metaDescription:
      "Si Catastro y tu escritura dicen superficies distintas, lo primero es saber cuál se aleja de la realidad. Te explicamos las causas y cómo comprobarlo con una medición técnica.",
    excerpt: "Dos documentos, dos cifras distintas. La realidad solo tiene una: se mide.",
    h1: "Catastro y escritura no coinciden",
    answer:
      "Que Catastro y la escritura atribuyan superficies distintas a la misma finca es muy frecuente, sobre todo en propiedades antiguas. Ninguna de las dos cifras es automáticamente la correcta: la referencia objetiva es la realidad del terreno, y esa solo se conoce midiendo. Hasta medir, cualquier conclusión es una suposición.",
    causes: [
      "Escrituras antiguas otorgadas «por cabida aparente»: la superficie se declaró sin medición real.",
      "Cartografía catastral histórica levantada con menor precisión que la actual.",
      "Segregaciones, compras de franjas o cambios de linderos documentados solo en una de las fuentes.",
      "Errores de digitalización o de representación gráfica en Catastro.",
    ],
    checks: [
      "Superficie real medida de la finca, con coordenadas georreferenciadas.",
      "Representación gráfica catastral: dónde dibuja Catastro la parcela y con qué geometría.",
      "Historial del título: escrituras anteriores, segregaciones o agrupaciones previas.",
      "Situación de los linderos físicos: muros, vallados, hitos.",
    ],
    professional:
      "La medición y el diagnóstico corresponden a un técnico competente en medición y cartografía. Si hay que corregir el título o inscribir una superficie distinta, intervienen además notario y Registro; la modificación de datos catastrales corresponde a la Gerencia de Catastro.",
    services: ["correccion-discrepancias-catastrales", "medicion-fincas-terrenos", "gml-catastro-registro"],
    relatedGuides: ["por-que-catastro-y-escritura-no-coinciden", "como-rectificar-superficie-catastro"],
  },
  {
    slug: "no-se-donde-termina-mi-finca",
    ogImage: "/og/problema-no-se-donde-termina-mi-finca.jpg",
    shortName: "No sé dónde termina mi finca",
    seoTitle: "No sé dónde terminan los límites de mi finca | MideFinca",
    metaDescription:
      "Si no sabes dónde empiezan y acaban los linderos de tu finca, la solución es medirla y contrastarla con la documentación. Te explicamos cómo se comprueba.",
    excerpt: "Muros movidos, hitos desaparecidos, colindancias ambiguas. Los límites se miden, no se adivinan.",
    h1: "No sé dónde terminan los límites de mi finca",
    answer:
      "Es una de las consultas más habituales: la escritura describe colindancias de otra época («al norte, tierras de…»), los hitos han desaparecido y el vallado actual puede no coincidir con el límite documental. La comprobación empieza midiendo la finca y comparando la geometría resultante con la escritura y con Catastro.",
    causes: [
      "Descripción de la finca por colindancias antiguas, sin geometría definida.",
      "Desaparición de mojones e hitos por obras, vegetación o paso del tiempo.",
      "Vallados y muros construidos por aproximación, no sobre el límite documental.",
      "Herencias y ventas parciales que nunca se replantearon sobre el terreno.",
    ],
    checks: [
      "Medición de la finca y de los elementos físicos de lindero existentes.",
      "Comparación de la geometría medida con la cabida de la escritura y la representación catastral.",
      "Estado de las fincas colindantes y sus representaciones gráficas.",
      "Existencia de hitos o restos de señalización antigua.",
    ],
    professional:
      "La localización técnica de los límites corresponde a un técnico de medición. Si el lindero se discute con un vecino y no hay acuerdo, el deslinde judicial corresponde a los tribunales, con intervención de abogados y peritos designados.",
    services: ["linderos-deslindes", "medicion-fincas-terrenos", "georreferenciacion-fincas"],
    relatedGuides: [],
  },
  {
    slug: "mi-finca-tiene-mas-o-menos-metros",
    ogImage: "/og/problema-mi-finca-tiene-mas-o-menos-metros.jpg",
    shortName: "Mi finca tiene más o menos metros de los que dice Catastro",
    seoTitle: "Mi finca tiene más o menos metros que en Catastro | MideFinca",
    metaDescription:
      "¿Sospechas que tu finca tiene más o menos metros de los que figuran en Catastro o en la escritura? Te explicamos por qué ocurre y cómo conocer la superficie real.",
    excerpt: "La duda no se resuelve con más documentos: se resuelve midiendo.",
    h1: "Mi finca tiene más o menos metros de los que dicen los papeles",
    answer:
      "Tanto si sospechas que tu finca tiene más metros de los declarados como si crees que le faltan, el camino es el mismo: medir. Una medición georreferenciada te da la superficie real y permite comparar objetivamente con Catastro y con la escritura, para saber qué fuente se aleja de la realidad y en cuánto.",
    causes: [
      "Si parece tener más: escrituras por cabida aparente conservadoras, errores antiguos de levantamiento catastral, o franjas ganadas por movimientos de lindero nunca documentados.",
      "Si parece tener menos: ventas o cesiones parciales no reflejadas en todos los registros, ocupaciones de colindantes, o una representación catastral que incluye terreno ajeno.",
      "En ambos casos: diferencias de criterio de medición entre épocas y fuentes.",
    ],
    checks: [
      "Superficie real medida sobre el terreno.",
      "Comparativa con la superficie catastral y la registral, cifra a cifra.",
      "Revisión del historial documental de la finca.",
      "Comprobación de que los linderos físicos coinciden con la geometría documental.",
    ],
    professional:
      "La determinación de la superficie real es trabajo técnico de medición. Las consecuencias (corregir Catastro, actualizar el título o reclamar a un tercero) pueden requerir además notario, Registro o asesoramiento jurídico, según el caso.",
    services: ["medicion-fincas-terrenos", "correccion-discrepancias-catastrales"],
    relatedGuides: ["como-saber-metros-reales-finca", "por-que-catastro-y-escritura-no-coinciden", "como-rectificar-superficie-catastro"],
  },
  {
    slug: "necesito-gml",
    ogImage: "/og/problema-necesito-gml.jpg",
    shortName: "Necesito un GML",
    seoTitle: "Me piden un archivo GML: qué es y cómo conseguirlo | MideFinca",
    metaDescription:
      "Si el notario o el Registro te piden un archivo GML de tu finca, te explicamos qué es, por qué lo exigen y cómo se elabora a partir de una medición real.",
    excerpt: "El notario lo pide, el Registro lo exige. Así se elabora un GML válido.",
    h1: "Me han pedido un archivo GML de mi finca",
    answer:
      "El GML es un archivo informático con la geometría georreferenciada de tu finca: la forma oficial de «dibujar» la propiedad sobre la cartografía. Notarías y Registro lo reclaman desde la Ley 13/2015 en compraventas, herencias, segregaciones y declaraciones de obra nueva. No se dibuja sobre una foto aérea: se construye a partir de coordenadas medidas y se valida con las herramientas de Catastro.",
    causes: [
      "Compraventa, herencia, donación o aportación de la finca.",
      "Segregación, división o agrupación de parcelas.",
      "Declaración o ampliación de obra nueva.",
      "Incorporación de la representación gráfica georreferenciada a la escritura o al Registro.",
    ],
    checks: [
      "Qué geometría debe contener el GML: parcela, construcción o ambas.",
      "Si la representación catastral actual coincide con la realidad (si no, conviene medir antes).",
      "Validez del archivo en las herramientas oficiales de Catastro.",
      "Coherencia de la geometría con la escritura y con las fincas colindantes.",
    ],
    professional:
      "La elaboración del GML a partir de medición real es trabajo técnico. La incorporación del archivo al expediente y sus efectos corresponden al notario y al registrador que tramitan tu caso.",
    services: ["gml-catastro-registro", "georreferenciacion-fincas"],
    relatedGuides: ["que-es-un-archivo-gml", "que-es-ivga-informe-validacion-grafica"],
  },
  {
    slug: "comprar-finca-comprobar-superficie",
    ogImage: "/og/problema-comprar-finca-comprobar-superficie.jpg",
    shortName: "Comprar una finca y comprobar los metros",
    seoTitle: "Comprar una finca: cómo comprobar los metros antes de firmar | MideFinca",
    metaDescription:
      "Antes de comprar una finca en Canarias, comprueba la superficie real, los límites y la coherencia con Catastro y escritura. Así evitas pagar metros que no existen.",
    excerpt: "La mejor herramienta de negociación al comprar una finca es una medición.",
    h1: "Quiero comprar una finca y comprobar los metros antes",
    answer:
      "Es la decisión más sensata. Anuncio, escritura y Catastro pueden decir tres cifras distintas, y la que cuenta para pagar es la real. Una comprobación previa mide la finca, verifica sus límites y compara todo con la documentación, antes de que firmes y las sorpresas pasen a ser tuyas.",
    causes: [
      "Superficies de anuncio tomadas de Catastro sin verificación.",
      "Escrituras antiguas por cabida aparente que el vendedor da por buenas.",
      "Linderos físicos que no coinciden con los documentales.",
      "Construcciones no declaradas que el comprador heredaría como problema.",
    ],
    checks: [
      "Medición de la superficie real de la parcela.",
      "Coherencia entre realidad, Catastro y escritura.",
      "Estado de linderos y posibles ocupaciones por colindantes.",
      "Existencia de construcciones y si constan documentadas.",
    ],
    professional:
      "La comprobación técnica previa a la compra corresponde a un técnico de medición. Si el informe detecta problemas documentales complejos, tu abogado o la notaría valoran cómo afectan a la operación.",
    services: ["comprobacion-finca-antes-comprar", "medicion-fincas-terrenos", "linderos-deslindes"],
    relatedGuides: ["como-saber-metros-reales-finca"],
  },
  {
    slug: "construccion-no-documentada",
    ogImage: "/og/problema-construccion-no-documentada.jpg",
    shortName: "Mi construcción no aparece en la escritura",
    seoTitle: "Mi construcción o ampliación no aparece en la escritura | MideFinca",
    metaDescription:
      "Si tu vivienda, ampliación o edificación no figura en la escritura o en Catastro, te explicamos qué documentación técnica necesitas para la declaración de obra nueva.",
    excerpt: "La construcción existe; los papeles no lo saben. Así se documenta.",
    h1: "Mi construcción o ampliación no aparece correctamente documentada",
    answer:
      "Viviendas enteras, segundas plantas, cerramientos de terraza, anexos…: es muy común que la realidad construida supere lo que dicen escritura y Catastro. El cauce habitual para documentar lo construido es la declaración (o ampliación) de obra nueva ante notario, y su base es técnica: medición, planos, certificado descriptivo y, normalmente, representación gráfica georreferenciada.",
    causes: [
      "Construcciones hechas sin escritura posterior que las recogiera.",
      "Ampliaciones ejecutadas hace años y nunca declaradas.",
      "Herencias de inmuebles cuya documentación nunca se actualizó.",
      "Edificaciones reflejadas en Catastro pero no en el título, o al revés.",
    ],
    checks: [
      "Qué está construido exactamente: superficie, ocupación, situación en la finca.",
      "Antigüedad acreditable de la construcción: fotos aéreas históricas, documentos de época.",
      "Situación urbanística del suelo, que condiciona las opciones.",
      "Qué figura en Catastro y en el Registro actualmente.",
    ],
    professional:
      "La medición, los planos y el certificado técnico corresponden al técnico; la declaración de obra nueva se otorga ante notario y se inscribe en el Registro. La antigüedad y la situación urbanística pueden requerir certificado específico y asesoramiento adicional. Cada caso tiene viabilidad distinta: debe estudiarse antes de prometer nada.",
    services: ["declaracion-obra-nueva", "ampliacion-obra-nueva", "certificado-antiguedad"],
    relatedGuides: [],
  },
{
    slug: "dividir-finca-en-parcelas",
    ogImage: "/og/problema-dividir-finca-en-parcelas.jpg",
    shortName: "Quiero dividir una finca en dos o más parcelas",
    seoTitle: "Quiero dividir una finca en dos o más parcelas | MideFinca",
    metaDescription:
      "Antes de segregar una finca hay que comprobar su situación registral, catastral y urbanística y definir correctamente las nuevas parcelas. Te explicamos qué se revisa.",
    excerpt: "Antes de dividir hay que comprobar la situación registral, catastral y urbanística de la finca.",
    h1: "Quiero dividir una finca en dos o más parcelas",
    answer:
      "Dividir una finca es posible en muchos casos, pero no es automático: la segregación queda condicionada a la normativa urbanística y a las autorizaciones que sean necesarias. Antes de plantearla hay que comprobar la situación registral, catastral y urbanística de la finca y definir técnicamente las nuevas parcelas: geometría, superficie y coordenadas de cada una.",
    causes: [
      "Herencias que hay que repartir entre varios herederos.",
      "Venta de solo una parte de la finca.",
      "Distribución de terrenos entre socios o familiares.",
      "Fincas demasiado grandes para el uso previsto de una parte.",
    ],
    checks: [
      "Situación registral: titularidad, cargas y descripción de la finca.",
      "Situación catastral: superficie y representación gráfica actual.",
      "Normativa urbanística: si la segregación es posible y con qué condiciones.",
      "Definición técnica de las nuevas parcelas antes de cualquier escritura.",
    ],
    professional:
      "La definición técnica de las nuevas parcelas y la representación gráfica corresponden al técnico. La escritura de la división es notarial, la inscripción de las fincas resultantes es registral y la autorización urbanística, cuando sea necesaria, corresponde al Ayuntamiento. Catastro actualiza su representación a partir de lo documentado.",
    services: ["segregacion-finca-canarias", "medicion-fincas-terrenos"],
    relatedGuides: ["como-segregar-una-finca-en-canarias"],
  },
  {
    slug: "unir-varias-fincas",
    ogImage: "/og/problema-unir-varias-fincas.jpg",
    shortName: "Quiero unir varias fincas en una sola",
    seoTitle: "Quiero unir varias fincas en una sola | MideFinca",
    metaDescription:
      "Agrupar fincas exige comprobar colindancia, titularidad y documentación, y definir la geometría de la finca resultante. Te explicamos qué se revisa antes de agrupar.",
    excerpt: "Unir fincas no es sumar superficies: hay que definir y documentar la finca resultante.",
    h1: "Quiero unir varias fincas en una sola",
    answer:
      "Unir dos o más fincas en una sola es una agrupación, y requiere definir técnicamente la finca resultante: su nueva geometría, su superficie y su representación gráfica. No todas las fincas pueden agruparse automáticamente: hay que comprobar que sean colindantes y revisar su titularidad, sus cargas y la normativa aplicable antes de confirmar la viabilidad.",
    causes: [
      "Compra de la finca vecina para unirla a la tuya.",
      "Varias parcelas heredadas que se quieren gestionar como una.",
      "Necesidad de una finca única para un proyecto o una operación.",
      "Simplificación de la documentación de varias propiedades.",
    ],
    checks: [
      "Colindancia y compatibilidad de las parcelas a agrupar.",
      "Titularidad y cargas registrales de cada finca.",
      "Coincidencia entre escrituras, Catastro y realidad física.",
      "Geometría y superficie de la finca resultante.",
    ],
    professional:
      "La medición, la definición de la finca resultante y la representación gráfica son trabajo técnico. La agrupación se formaliza normalmente con escritura ante Notaría y se inscribe en el Registro de la Propiedad; después se actualiza Catastro.",
    services: ["agrupacion-fincas-canarias", "medicion-fincas-terrenos"],
    relatedGuides: ["como-agrupar-fincas-en-canarias"],
  },
  {
    slug: "primera-ocupacion-fuera-de-ordenacion",
    ogImage: "/og/problema-primera-ocupacion-fuera-de-ordenacion.jpg",
    shortName: "Necesito la primera ocupación de una edificación fuera de ordenación",
    seoTitle: "Primera ocupación de una edificación fuera de ordenación | MideFinca",
    metaDescription:
      "Si tu edificación está fuera de ordenación y necesitas tramitar su primera ocupación, hay que estudiar el régimen urbanístico del inmueble y documentar su estado actual. Te explicamos cómo.",
    excerpt: "El trámite exige documentar qué existe, desde cuándo y en qué condiciones está la edificación.",
    h1: "Necesito la primera ocupación de una edificación fuera de ordenación",
    answer:
      "Cuando una edificación está fuera de ordenación, tramitar su primera ocupación o utilización exige primero estudiar el régimen urbanístico del inmueble para comprobar si el trámite es posible, y después documentar técnicamente el estado actual de la edificación, su antigüedad y sus condiciones. La resolución corresponde siempre al Ayuntamiento competente.",
    causes: [
      "Edificaciones construidas con normativa anterior o sin ella.",
      "Inmuebles que necesitan acreditar sus condiciones para contratar o regularizar suministros.",
      "Compraventas en las que piden acreditar la situación de la edificación.",
      "Expedientes municipales que requieren documentación del estado actual.",
    ],
    checks: [
      "Régimen urbanístico del inmueble y si permite el trámite.",
      "Antigüedad de la edificación y sus evidencias.",
      "Estado actual: medición, planos y condiciones.",
      "Documentación de habitabilidad, en viviendas y cuando corresponda.",
    ],
    professional:
      "El estudio, la medición, los planos y los informes o certificados técnicos son nuestra parte. La primera ocupación se resuelve por el Ayuntamiento conforme a la normativa urbanística; no podemos garantizar su concesión.",
    services: ["primera-ocupacion-fuera-ordenacion", "certificado-habitabilidad", "certificado-antiguedad"],
    relatedGuides: ["primera-ocupacion-edificaciones-fuera-de-ordenacion"],
  },
  {
    slug: "acreditar-uso-consolidado",
    ogImage: "/og/problema-acreditar-uso-consolidado.jpg",
    shortName: "Necesito acreditar que el uso de mi inmueble está consolidado",
    seoTitle: "Necesito acreditar el uso consolidado de mi inmueble | MideFinca",
    metaDescription:
      "Acreditar un uso consolidado exige demostrar con evidencias qué uso tiene la edificación, desde cuándo y en qué condiciones. Te explicamos cómo se documenta.",
    excerpt: "El uso consolidado se demuestra con evidencias: antigüedad, características y condiciones del uso actual.",
    h1: "Necesito acreditar el uso consolidado de un inmueble",
    answer:
      "Para acreditar un uso consolidado hay que demostrar con evidencias qué uso tiene la edificación, desde cuándo existe ese uso y en qué condiciones se desarrolla. Un informe técnico documenta esa realidad de forma objetiva; el reconocimiento administrativo del uso, cuando sea necesario, corresponde a la Administración conforme a la normativa urbanística aplicable.",
    causes: [
      "El uso real del inmueble no coincide con el que figura en la documentación.",
      "Una administración o un procedimiento pide acreditar el uso consolidado.",
      "Compraventas o regularizaciones en las que el uso actual debe quedar documentado.",
      "Usos que llevan años desarrollándose sin constancia documental suficiente.",
    ],
    checks: [
      "Uso actual de la edificación y sus características.",
      "Antigüedad del uso: fotografía aérea histórica y documentación de época.",
      "Condiciones del inmueble y planos del estado actual, cuando proceda.",
      "Normativa urbanística aplicable al caso concreto.",
    ],
    professional:
      "La documentación técnica del uso existente es nuestra parte: informe, medición, planos y evidencias. La valoración urbanística y el reconocimiento del uso consolidado corresponden a la Administración competente.",
    services: ["informe-uso-consolidado", "certificado-antiguedad"],
    relatedGuides: ["que-es-uso-consolidado-canarias"],
  },
  {
    slug: "que-puedo-construir-en-parcela",
    ogImage: "/og/problema-que-puedo-construir-en-parcela.jpg",
    shortName: "Quiero saber qué puedo construir en una parcela",
    seoTitle: "Qué puedo construir en una parcela: cómo saberlo | MideFinca",
    metaDescription:
      "Antes de comprar o proyectar conviene conocer la clasificación del suelo, los usos admitidos y los parámetros de edificación. La cédula urbanística es el documento oficial que lo acredita.",
    excerpt: "Lo que puedes construir lo define el planeamiento: clasificación del suelo, usos y parámetros.",
    h1: "Quiero saber qué puedo construir o hacer urbanísticamente en una parcela",
    answer:
      "Lo que puede construirse en una parcela lo define el planeamiento municipal: la clasificación y categorización del suelo, los usos admitidos y los parámetros de edificación. El documento oficial que lo acredita es la cédula o información urbanística, que expide el Ayuntamiento. Nosotros preparamos y tramitamos la solicitud con la documentación técnica de la parcela.",
    causes: [
      "Compra de una parcela sin conocer sus posibilidades reales.",
      "Proyectos que se quieren plantear sin confirmar las condiciones urbanísticas.",
      "Dudas sobre usos permitidos o parámetros de edificación.",
      "Requerimientos de notarías, bancos o administraciones que piden información urbanística oficial.",
    ],
    checks: [
      "Clasificación y categorización del suelo de la parcela.",
      "Usos admitidos y condiciones de edificación.",
      "Parámetros urbanísticos: ocupación, edificabilidad, alturas, retranqueos.",
      "Coherencia entre la información urbanística y la realidad física del terreno.",
    ],
    professional:
      "La información urbanística oficial la expide el Ayuntamiento. Nuestra parte es identificar y documentar correctamente la parcela, preparar y presentar la solicitud, y explicarte el contenido de la respuesta municipal en términos claros.",
    services: ["cedula-urbanistica", "alineaciones-rasantes"],
    relatedGuides: ["que-es-cedula-urbanistica"],
  },
  {
    slug: "alineaciones-y-rasantes",
    ogImage: "/og/problema-alineaciones-y-rasantes.jpg",
    shortName: "Me piden las alineaciones y rasantes de una parcela",
    seoTitle: "Me piden las alineaciones y rasantes de una parcela | MideFinca",
    metaDescription:
      "Si te piden las alineaciones y rasantes de una parcela, necesitas documentación topográfica precisa y, cuando proceda, la resolución municipal. Te explicamos qué son y cómo se obtienen.",
    excerpt: "La alineación marca hasta dónde puede llegar la edificación; la rasante fija su cota de referencia.",
    h1: "Me piden las alineaciones y rasantes de una parcela",
    answer:
      "Las alineaciones y rasantes definen la posición y la cota que condicionan la futura edificación respecto de la vía pública. Cuando el expediente lo requiere, las expide oficialmente el Ayuntamiento; nuestra parte es la documentación técnica y topográfica: el levantamiento preciso de la parcela y de la vía, los planos y la preparación de la solicitud.",
    causes: [
      "Proyectos de nueva edificación que deben respetar la alineación oficial.",
      "Trámites de licencia que requieren las alineaciones y rasantes.",
      "Parcelas que lindan con la vía pública sin definición clara de su posición respecto a ella.",
      "Dudas sobre la cota a la que debe construirse.",
    ],
    checks: [
      "Levantamiento topográfico de la parcela y de su entorno respecto de la vía.",
      "Documentación municipal existente sobre alineaciones previas.",
      "Cotas de la vía y del terreno para definir la rasante.",
      "Procedimiento municipal aplicable al caso.",
    ],
    professional:
      "El levantamiento topográfico y la documentación técnica son trabajo nuestro. La certificación oficial de alineaciones y rasantes, cuando el procedimiento la requiere, la emite el Ayuntamiento.",
    services: ["alineaciones-rasantes", "levantamiento-topografico"],
    relatedGuides: ["que-son-alineaciones-y-rasantes"],
  },
  {
    slug: "que-permiso-necesito-reforma",
    ogImage: "/og/problema-que-permiso-necesito-reforma.jpg",
    shortName: "Quiero hacer una reforma y no sé qué permiso necesito",
    seoTitle: "Quiero hacer una reforma y no sé qué permiso necesito | MideFinca",
    metaDescription:
      "Comunicación previa o licencia de obra: qué procedimiento corresponde depende del tipo de obra y de la ordenanza de cada municipio. Te explicamos cómo se comprueba.",
    excerpt: "No toda obra pequeña es comunicación previa ni toda reforma exige licencia: depende de la ordenanza municipal.",
    h1: "Quiero hacer una reforma y no sé qué permiso necesito",
    answer:
      "El procedimiento que corresponde a una reforma depende del tipo de actuación y de la ordenanza de cada Ayuntamiento: unas obras pueden tramitarse por comunicación previa y otras exigen licencia urbanística con proyecto técnico. Comprobarlo antes de empezar evita presentar el procedimiento equivocado y sus consecuencias.",
    causes: [
      "Reformas de viviendas o locales sin saber qué trámite exige el municipio.",
      "Actuaciones que afectan o no a elementos estructurales o a la fachada.",
      "Obras iniciadas sin permiso que hay que regularizar.",
      "Indicaciones municipales que piden documentación técnica para la reforma.",
    ],
    checks: [
      "Tipo de actuación y si afecta a estructura, fachada o distribución.",
      "Ordenanza municipal aplicable: comunicación previa o licencia.",
      "Documentación técnica que exige el procedimiento correspondiente.",
      "Si la actuación necesita proyecto técnico o basta una memoria sencilla.",
    ],
    professional:
      "La comprobación del procedimiento y la documentación técnica son nuestra parte: memoria, planos, medición y, cuando la actuación lo exige, proyecto. La autorización, en cualquiera de los dos procedimientos, corresponde al Ayuntamiento.",
    services: ["comunicacion-previa-obra", "licencia-obra-mayor"],
    relatedGuides: ["comunicacion-previa-obra-cuando-se-utiliza"],
  },
  {
    slug: "obra-con-licencia-municipal",
    ogImage: "/og/problema-obra-con-licencia-municipal.jpg",
    shortName: "Voy a hacer una obra que necesita licencia municipal",
    seoTitle: "Voy a hacer una obra y necesito licencia municipal | MideFinca",
    metaDescription:
      "Obra nueva, ampliaciones, rehabilitaciones o intervenciones estructurales: cuando la actuación exige licencia de obra mayor hace falta un proyecto técnico. Te explicamos qué documentación se prepara.",
    excerpt: "Cuando la actuación exige licencia, el expediente necesita un proyecto técnico completo.",
    h1: "Voy a hacer una obra que necesita licencia municipal",
    answer:
      "Las actuaciones que exigen licencia de obra mayor necesitan un proyecto técnico y la autorización municipal previa: dependiendo del caso, obra nueva, ampliaciones, determinadas reformas, rehabilitaciones o intervenciones estructurales. El proyecto y la documentación del expediente se preparan antes de presentar la solicitud, y la concesión corresponde al Ayuntamiento.",
    causes: [
      "Construcción de una edificación nueva.",
      "Ampliación de una construcción existente.",
      "Reformas que afectan a la estructura o a la configuración del inmueble.",
      "Rehabilitaciones o intervenciones estructurales.",
    ],
    checks: [
      "Normativa urbanística aplicable a la parcela y a la actuación.",
      "Medición y levantamiento del estado actual, cuando proceda.",
      "Alcance del proyecto técnico exigible según la obra.",
      "Documentación que el Ayuntamiento requiere para el expediente.",
    ],
    professional:
      "El estudio previo, la medición, el proyecto técnico y la presentación de la solicitud son nuestra parte. La licencia la concede o deniega el Ayuntamiento conforme a la normativa urbanística; por eso no se puede garantizar su concesión, sí un expediente técnicamente bien planteado.",
    services: ["licencia-obra-mayor"],
    relatedGuides: ["licencia-obra-mayor-documentacion"],
  },
  {
    slug: "dividir-edificio-en-unidades",
    ogImage: "/og/problema-dividir-edificio-en-unidades.jpg",
    shortName: "Quiero dividir un edificio en unidades independientes",
    seoTitle: "Quiero dividir un edificio en varias unidades | MideFinca",
    metaDescription:
      "Para dividir un edificio en viviendas o locales independientes hace falta medirlo, describir cada unidad y fijar sus cuotas. Te explicamos qué se revisa antes de escriturar.",
    excerpt: "Dividir un edificio en unidades exige medirlo y describir cada parte antes de escriturar.",
    h1: "Quiero dividir un edificio en varias unidades independientes",
    answer:
      "Dividir un edificio en unidades independientes se formaliza mediante la división horizontal, y su parte técnica debe estar medida y bien descrita antes de escriturar: superficie y distribución de cada unidad, elementos comunes y cuotas de participación. Si el título se redacta sobre datos aproximados, las discrepancias aparecen después, en la venta o en el reparto.",
    causes: [
      "Herencias sobre un edificio que hay que repartir entre varios herederos.",
      "Edificios familiares que se quieren convertir en viviendas independientes.",
      "Promociones pequeñas que venden cada unidad por separado.",
      "Títulos constitutivos antiguos que ya no reflejan la realidad construida.",
    ],
    checks: [
      "Medición real del edificio y de cada unidad, no solo de los planos antiguos.",
      "Descripción de los elementos comunes y de qué parte corresponde a cada unidad.",
      "Cuotas de participación coherentes con la medición.",
      "Situación registral y catastral previa del inmueble.",
    ],
    professional:
      "La medición y la documentación técnica de las unidades corresponden al técnico. La escritura de la división horizontal es notarial y su inscripción, registral. Si el título constitutivo o los estatutos existentes se ven afectados, pueden ser necesarios consentimientos adicionales según el caso.",
    services: ["division-horizontal-canarias", "certificado-superficies"],
    relatedGuides: ["que-es-division-horizontal"],
  },
  {
    slug: "convertir-local-en-vivienda",
    ogImage: "/og/problema-convertir-local-en-vivienda.jpg",
    shortName: "Quiero convertir un local en vivienda",
    seoTitle: "Quiero convertir un local en vivienda: qué comprobar | MideFinca",
    metaDescription:
      "No todos los locales pueden convertirse en vivienda: depende del planeamiento, de la ordenanza municipal y de las condiciones del propio local. Te explicamos qué se comprueba primero.",
    excerpt: "Antes de obrar o comprar, hay que comprobar si el local y la normativa permiten el cambio de uso.",
    h1: "Quiero convertir un local en vivienda",
    answer:
      "Convertir un local en vivienda no es solo una reforma: es un cambio de uso que debe estar permitido por el planeamiento y por la ordenanza municipal, y que exige que el espacio reúna condiciones concretas de habitabilidad, como iluminación y ventilación natural o alturas mínimas. Por eso lo primero no es la obra, sino el estudio de viabilidad.",
    causes: [
      "Locales en desuso cuyo valor sería mayor como vivienda.",
      "Compras de bajos comerciales pensando ya en transformarlos.",
      "Necesidad de aprovechar un espacio propio para uso residencial.",
      "Inversión en zonas consolidadas donde la vivienda tiene más demanda.",
    ],
    checks: [
      "Planeamiento y ordenanza municipal: si el cambio de uso es admisible en esa parcela o edificio.",
      "Condiciones físicas del local: huecos a espacio libre o patio, ventilación, altura libre y distribución.",
      "Estatutos de la comunidad de propietarios, si el local forma parte de un edificio dividido horizontalmente.",
      "Procedimiento aplicable: comunicación previa, declaración responsable o licencia, según el municipio y el alcance de la actuación.",
    ],
    professional:
      "El estudio de viabilidad y la documentación técnica corresponden al técnico. La admisibilidad urbanística y la resolución del procedimiento corresponden al Ayuntamiento; cuando el local está en un edificio en propiedad horizontal, conviene revisar además los estatutos de la comunidad.",
    services: ["cambio-de-uso-local-a-vivienda", "certificado-habitabilidad", "licencia-obra-mayor"],
    relatedGuides: ["cambio-de-uso-local-a-vivienda-canarias"],
  },
];
